import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export interface BitcrusherParameters {
  readonly bitDepth: number;
  readonly rateHz: number;
  readonly dither: number;
  readonly ditherSeed: number;
  readonly antiAliasPrefilter: number;
}

export interface BitcrusherOptions {
  readonly sampleRate: number;
  readonly bitDepth?: number;
  readonly rateHz?: number;
  readonly dither?: boolean | number;
  readonly ditherSeed?: number;
  readonly antiAliasPrefilter?: boolean | number;
}

type ParameterKey = keyof BitcrusherParameters;
type ParameterId = 'bit-depth' | 'rate-hz' | 'dither' | 'dither-seed' | 'anti-alias-prefilter';

export const PARAMETER_BOUNDS = Object.freeze({
  bitDepth: Object.freeze({ min: 1, max: 24, unit: 'bit' }),
  rateHz: Object.freeze({ min: 1, max: 192_000, unit: 'Hz' }),
  dither: Object.freeze({ min: 0, max: 1, unit: 'boolean' }),
  ditherSeed: Object.freeze({ min: 0, max: 0xffff_ffff, unit: 'integer' }),
  antiAliasPrefilter: Object.freeze({ min: 0, max: 1, unit: 'boolean' }),
} as const);

export const DEFAULT_BITCRUSHER_PARAMETERS: Readonly<BitcrusherParameters> = Object.freeze({
  bitDepth: 16,
  rateHz: 192_000,
  dither: 0,
  ditherSeed: 0,
  antiAliasPrefilter: 0,
});

const ID_TO_KEY: Readonly<Record<ParameterId, ParameterKey>> = Object.freeze({
  'bit-depth': 'bitDepth',
  'rate-hz': 'rateHz',
  dither: 'dither',
  'dither-seed': 'ditherSeed',
  'anti-alias-prefilter': 'antiAliasPrefilter',
});

const clamp = (value: number, minimum: number, maximum: number): number => Math.max(minimum, Math.min(maximum, value));

const finite = (label: string, value: number): void => {
  if (!Number.isFinite(value)) throw new RangeError(`${label} must be finite`);
};

const booleanNumber = (label: string, value: boolean | number): number => {
  const numeric = typeof value === 'boolean' ? Number(value) : value;
  finite(label, numeric);
  if (numeric !== 0 && numeric !== 1) throw new RangeError(`${label} must be 0 or 1`);
  return numeric;
};

const roundedClamped = (label: string, value: number, minimum: number, maximum: number): number => {
  finite(label, value);
  return clamp(Math.round(value), minimum, maximum);
};

/**
 * Stateful mono bitcrusher and sample-rate reducer.
 *
 * Quantization is signed two's-complement PCM: for B bits, step = 2^(1-B),
 * codes are [-2^(B-1), 2^(B-1)-1], and exact half-step ties round away from
 * zero. This gives exactly 2^B levels, represents silence exactly, maps -1 to
 * -1, and reserves 1-step as the largest positive code (the normal signed-PCM
 * asymmetry). Inputs are saturated only at this quantizer boundary.
 *
 * Rate reduction is causal zero-order hold. With the prefilter disabled,
 * frequencies above the reduced Nyquist intentionally alias for the classic
 * lo-fi effect. The optional second-order Butterworth low-pass at 45% of the
 * held rate is the anti-alias policy. It is bypassed when no rate reduction is
 * active. Optional dither is deterministic TPDF with a peak amplitude of one
 * quantization step and is applied before rounding.
 */
export class BitcrusherSampleRateReducer {
  readonly sampleRate: number;
  private readonly values: BitcrusherParameters;
  private randomState = 0;
  private phase = 1;
  private heldSample = 0;
  private filterX1 = 0;
  private filterX2 = 0;
  private filterY1 = 0;
  private filterY2 = 0;
  private filterB0 = 1;
  private filterB1 = 0;
  private filterB2 = 0;
  private filterA1 = 0;
  private filterA2 = 0;

  constructor(options: BitcrusherOptions) {
    finite('sampleRate', options.sampleRate);
    if (options.sampleRate < 1 || options.sampleRate > 768_000) {
      throw new RangeError('sampleRate must be in [1, 768000]');
    }
    this.sampleRate = options.sampleRate;
    this.values = {
      bitDepth: roundedClamped('bitDepth', options.bitDepth ?? DEFAULT_BITCRUSHER_PARAMETERS.bitDepth, 1, 24),
      rateHz: clampFinite('rateHz', options.rateHz ?? DEFAULT_BITCRUSHER_PARAMETERS.rateHz, 1, 192_000),
      dither: booleanNumber('dither', options.dither ?? DEFAULT_BITCRUSHER_PARAMETERS.dither),
      ditherSeed: roundedClamped('ditherSeed', options.ditherSeed ?? DEFAULT_BITCRUSHER_PARAMETERS.ditherSeed, 0, 0xffff_ffff),
      antiAliasPrefilter: booleanNumber('antiAliasPrefilter', options.antiAliasPrefilter ?? DEFAULT_BITCRUSHER_PARAMETERS.antiAliasPrefilter),
    };
    this.updateFilterCoefficients();
    this.reset();
  }

  get bitDepth(): number { return this.values.bitDepth; }
  get effectiveRateHz(): number { return Math.min(this.values.rateHz, this.sampleRate); }
  get ditherSeed(): number { return this.values.ditherSeed; }
  get latencySamples(): number { return 0; }
  get latencySeconds(): number { return 0; }

  reset(): void {
    this.randomState = this.values.ditherSeed >>> 0;
    this.phase = 1;
    this.heldSample = 0;
    this.filterX1 = 0;
    this.filterX2 = 0;
    this.filterY1 = 0;
    this.filterY2 = 0;
  }

  setParameter(id: string, value: number): void {
    const key = ID_TO_KEY[id as ParameterId];
    if (!key) throw new RangeError(`unknown parameter: ${id}`);
    const mutable = this.values as { -readonly [K in ParameterKey]: number };
    if (key === 'dither' || key === 'antiAliasPrefilter') {
      mutable[key] = booleanNumber(id, value);
    } else if (key === 'bitDepth') {
      mutable.bitDepth = roundedClamped(id, value, 1, 24);
    } else if (key === 'ditherSeed') {
      mutable.ditherSeed = roundedClamped(id, value, 0, 0xffff_ffff);
      this.randomState = mutable.ditherSeed >>> 0;
    } else {
      mutable.rateHz = clampFinite(id, value, 1, 192_000);
    }
    if (key === 'rateHz' || key === 'antiAliasPrefilter') this.updateFilterCoefficients();
  }

  private updateFilterCoefficients(): void {
    if (this.values.antiAliasPrefilter !== 1 || this.effectiveRateHz >= this.sampleRate) {
      this.filterB0 = 1; this.filterB1 = 0; this.filterB2 = 0; this.filterA1 = 0; this.filterA2 = 0;
      return;
    }
    const cutoff = Math.min(this.effectiveRateHz * 0.45, this.sampleRate * 0.499);
    const omega = 2 * Math.PI * cutoff / this.sampleRate;
    const cosine = Math.cos(omega);
    const alpha = Math.sin(omega) / Math.SQRT2;
    const a0 = 1 + alpha;
    this.filterB0 = (1 - cosine) * 0.5 / a0;
    this.filterB1 = (1 - cosine) / a0;
    this.filterB2 = this.filterB0;
    this.filterA1 = -2 * cosine / a0;
    this.filterA2 = (1 - alpha) / a0;
  }

  processInto(input: Float32Array, output: Float32Array, frames = output.length): void {
    if (!Number.isInteger(frames) || frames < 0 || input.length < frames || output.length < frames) {
      throw new RangeError('buffers are too short for frames');
    }

    for (let frame = 0; frame < frames; frame += 1) {
      if (!Number.isFinite(input[frame]!)) throw new RangeError('input samples must be finite');
    }

    const rate = this.effectiveRateHz;
    const increment = rate / this.sampleRate;
    const useFilter = this.values.antiAliasPrefilter === 1 && rate < this.sampleRate;
    const scale = 2 ** (this.values.bitDepth - 1);
    const minimumCode = -scale;
    const maximumCode = scale - 1;
    let phase = this.phase;
    let held = this.heldSample;
    let x1 = this.filterX1;
    let x2 = this.filterX2;
    let y1 = this.filterY1;
    let y2 = this.filterY2;
    let randomState = this.randomState;

    for (let frame = 0; frame < frames; frame += 1) {
      const inputSample = input[frame]!;
      let candidate = inputSample;
      if (useFilter) {
        candidate = this.filterB0 * inputSample + this.filterB1 * x1 + this.filterB2 * x2
          - this.filterA1 * y1 - this.filterA2 * y2;
        x2 = x1;
        x1 = inputSample;
        y2 = y1;
        y1 = candidate;
      }

      if (phase >= 1) {
        phase -= 1;
        let scaled = clamp(candidate, -1, 1) * scale;
        if (this.values.dither === 1) {
          let uniform: number;
          [randomState, uniform] = nextUniform(randomState);
          scaled += uniform;
          [randomState, uniform] = nextUniform(randomState);
          scaled += uniform - 1;
        }
        const rounded = scaled < 0 ? Math.ceil(scaled - 0.5) : Math.floor(scaled + 0.5);
        held = clamp(rounded, minimumCode, maximumCode) / scale;
      }
      output[frame] = held;
      phase += increment;
    }

    this.phase = phase;
    this.heldSample = held;
    this.filterX1 = x1;
    this.filterX2 = x2;
    this.filterY1 = y1;
    this.filterY2 = y2;
    this.randomState = randomState;
  }
}

const clampFinite = (label: string, value: number, minimum: number, maximum: number): number => {
  finite(label, value);
  return clamp(value, minimum, maximum);
};

/** Mulberry32 step. Conversion uses [0, 1), including seed zero safely. */
const nextUniform = (state: number): [number, number] => {
  const next = (state + 0x6d2b79f5) >>> 0;
  let value = next;
  value = Math.imul(value ^ (value >>> 15), value | 1);
  value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
  return [next, ((value ^ (value >>> 14)) >>> 0) / 0x1_0000_0000];
};

class BitcrusherModuleInstance implements DspModuleInstance {
  private readonly processor: BitcrusherSampleRateReducer;
  constructor(sampleRate: number, private readonly maxBlockSize: number) {
    this.processor = new BitcrusherSampleRateReducer({ sampleRate });
  }
  reset(): void { this.processor.reset(); }
  setParameter(id: string, value: number): void { this.processor.setParameter(id, value); }
  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) {
      throw new RangeError('frames exceeds the configured maximum block size');
    }
    const input = block.inputs.in;
    const output = block.outputs.out;
    if (!input || input.length < block.frames) throw new RangeError('in buffer is missing or too short');
    if (!output || output.length < block.frames) throw new RangeError('out buffer is missing or too short');
    this.processor.processInto(input, output, block.frames);
  }
}

export const mod026 = defineDspModule({
  descriptor: {
    id: 'mir.mod-026',
    name: 'Bitcrusher and sample-rate reducer',
    version: '0.1.0',
    inputs: [{ id: 'in', kind: 'audio', channels: 1 }],
    outputs: [{ id: 'out', kind: 'audio', channels: 1 }],
    parameters: [
      { id: 'bit-depth', defaultValue: 16, minValue: 1, maxValue: 24, unit: 'bit' },
      { id: 'rate-hz', defaultValue: 192_000, minValue: 1, maxValue: 192_000, unit: 'Hz' },
      { id: 'dither', defaultValue: 0, minValue: 0, maxValue: 1, unit: 'boolean' },
      { id: 'dither-seed', defaultValue: 0, minValue: 0, maxValue: 0xffff_ffff, unit: 'integer' },
      { id: 'anti-alias-prefilter', defaultValue: 0, minValue: 0, maxValue: 1, unit: 'boolean' },
    ],
  },
  createInstance(context) {
    if (!Number.isFinite(context.sampleRate) || context.sampleRate < 1 || context.sampleRate > 768_000
      || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) {
      throw new RangeError('invalid DSP instance context');
    }
    return new BitcrusherModuleInstance(context.sampleRate, context.maxBlockSize);
  },
});
